/**
 * IncidentAvoidance turns the incident layer's features into a "danger" signal for routing.
 *
 * Two responsibilities:
 *   1. A penalty term fed into RouteScorer (the primary, reliable mechanism).
 *   2. An optional, best-effort waypoint "nudge" that tries to push a route around a dense incident
 *      cluster it passes through.
 *
 * IMPORTANT — best effort only: Google's Directions API has no concept of an avoided area/polygon.
 * Adding a single lateral waypoint only *suggests* a detour; Google may still route back through the
 * cluster, and over-nudging produces zig-zags. True area avoidance needs a dedicated routing engine
 * (Valhalla `exclude_polygons`, GraphHopper `block_area`). This module is intentionally isolated so
 * such an engine can later be swapped in behind the same call sites without touching scoring or UI.
 */

import { featureLatLng, minDistanceToPath, computeOffset, computeHeading, centroid } from "./GeoUtils";

const SECONDS_PER_MONTH = 30 * 24 * 60 * 60;

/**
 * Parses the GeoJSON `value_eur` property, which arrives as a string ("12000" or "-").
 * @returns {number} euros, or 0 when unknown.
 */
export function parseEur(value) {
  if (value == null) {
    return 0;
  }
  const n = Number(String(value).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Age of an incident in months relative to `nowSeconds`.
 */
function ageMonths(feature, nowSeconds) {
  const ts = Number(feature.getProperty("date_incident"));
  if (!Number.isFinite(ts) || ts <= 0) {
    return Infinity;
  }
  return (nowSeconds - ts) / SECONDS_PER_MONTH;
}

/**
 * Keeps only the incidents that pass the recency and minimum-severity filters.
 *
 * @param {google.maps.Data.Feature[]} incidentFeatures
 * @param {{incidentRecencyMonths: ?number, incidentMinSeverity: number}} config
 * @param {number} nowSeconds
 * @returns {google.maps.Data.Feature[]}
 */
export function filterIncidents(incidentFeatures, config, nowSeconds) {
  return incidentFeatures.filter((feature) => {
    if (config.incidentMinSeverity > 0 && parseEur(feature.getProperty("value_eur")) < config.incidentMinSeverity) {
      return false;
    }
    if (config.incidentRecencyMonths != null && ageMonths(feature, nowSeconds) > config.incidentRecencyMonths) {
      return false;
    }
    return true;
  });
}

/**
 * Relevance weight of a single incident: higher value and more recent → more weight.
 */
export function incidentWeight(feature, nowSeconds) {
  const severityFactor = 1 + Math.min(parseEur(feature.getProperty("value_eur")) / 100000, 4); // 1..5
  const age = ageMonths(feature, nowSeconds);
  const recencyFactor = Number.isFinite(age) ? Math.max(0.3, 1 - age / 36) : 0.3; // 1 (fresh) .. 0.3 (old)
  return severityFactor * recencyFactor;
}

/**
 * Total danger penalty for a route and the count of incidents within the danger radius.
 *
 * @param {google.maps.LatLng[]} densePath route corridor reference (already densified)
 * @param {google.maps.Data.Feature[]} incidents pre-filtered incident features
 * @param {{dangerRadius: number}} config dangerRadius in metres
 * @param {number} nowSeconds
 * @returns {{penalty: number, nearCount: number, near: google.maps.Data.Feature[]}}
 */
export function incidentPenalty(densePath, incidents, config, nowSeconds) {
  let penalty = 0;
  const near = [];
  incidents.forEach((feature) => {
    const { distance } = minDistanceToPath(featureLatLng(feature), densePath);
    if (distance <= config.dangerRadius) {
      penalty += incidentWeight(feature, nowSeconds);
      near.push(feature);
    }
  });
  return { penalty, nearCount: near.length, near };
}

/**
 * Best-effort: if the route passes through a dense incident cluster, produce up to `maxNudges` lateral
 * waypoints on the side of the route away from the cluster centroid, to suggest a detour around it.
 * Caller must re-request the route with these waypoints and keep the result only if the penalty
 * actually drops and the detour stays acceptable.
 *
 * @param {google.maps.LatLng[]} densePath
 * @param {google.maps.Data.Feature[]} nearIncidents incidents already known to be near the route
 * @param {{dangerRadius: number}} config
 * @param {number} maxNudges
 * @returns {google.maps.LatLng[]}
 */
export function computeNudgeWaypoints(densePath, nearIncidents, config, maxNudges = 1) {
  if (!nearIncidents.length || densePath.length < 2) {
    return [];
  }

  // Group near-incidents by the route vertex they are closest to, then take the densest group(s).
  const buckets = new Map();
  nearIncidents.forEach((feature) => {
    const { index } = minDistanceToPath(featureLatLng(feature), densePath);
    if (!buckets.has(index)) {
      buckets.set(index, []);
    }
    buckets.get(index).push(featureLatLng(feature));
  });

  const ranked = [...buckets.entries()].sort((a, b) => b[1].length - a[1].length).slice(0, maxNudges);

  const nudges = [];
  ranked.forEach(([index, points]) => {
    if (points.length < 2) {
      return; // a single incident is not a cluster worth a detour
    }
    const anchor = densePath[index];
    const next = densePath[Math.min(index + 1, densePath.length - 1)];
    const routeHeading = computeHeading(anchor, next);
    const clusterCentroid = centroid(points);
    const towardCluster = computeHeading(anchor, clusterCentroid);
    // Push to whichever perpendicular side faces away from the cluster.
    const leftPerp = routeHeading - 90;
    const diff = ((towardCluster - leftPerp + 540) % 360) - 180; // signed angle to the left perpendicular
    const awayHeading = diff > 0 ? routeHeading + 90 : routeHeading - 90;
    const offset = config.dangerRadius * 1.5;
    nudges.push(computeOffset(anchor, offset, awayHeading));
  });

  return nudges;
}
